import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BulkTransitionTimesheetsRequest,
  GetTimesheetRequest,
  TimesheetDto,
  TimesheetRequestDto,
  UpdateExternalTimesheetRequest,
} from 'src/api/FiretrolApiClient';
import { timesheetApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { invalidateFor } from 'src/api/queries/invalidationMap';

/**
 * Create and update share one endpoint; the response is seeded into the detail
 * cache so the editor keeps the server's recalculated rows and rates.
 */
export function useUpsertTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (timesheet: TimesheetRequestDto) =>
      timesheetApiClient.createTimesheet(timesheet),
    meta: { suppressErrorToast: true, errorContext: 'save timesheet' },
    onSuccess: (result: TimesheetDto) => {
      qc.setQueryData(
        queryKeys.timesheets.detail(
          result.jobId,
          result.userId,
          result.year ?? -1,
          result.weekId,
        ),
        result,
      );
    },
    onSettled: () => invalidateFor('timesheet.write', qc),
  });
}

export function useSendTimesheetNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: GetTimesheetRequest) =>
      timesheetApiClient.sendTimesheetNotification(request),
    meta: { suppressErrorToast: true, errorContext: 'resend timesheet email' },
    onSettled: () => invalidateFor('timesheet.write', qc),
  });
}

export function useBulkTransitionTimesheets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: BulkTransitionTimesheetsRequest) =>
      timesheetApiClient.bulkTransitionTimesheets(request),
    meta: { suppressErrorToast: true, errorContext: 'transition timesheets' },
    onSettled: () => invalidateFor('timesheet.write', qc),
  });
}

/** Rejection reuses the external-review endpoint with no token (admin context). */
export function useRejectTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      timesheetId,
      request,
    }: {
      timesheetId: string;
      request: UpdateExternalTimesheetRequest;
    }) => timesheetApiClient.updateExternal(timesheetId, undefined, request),
    meta: { suppressErrorToast: true, errorContext: 'reject timesheet' },
    onSettled: () => invalidateFor('timesheet.write', qc),
  });
}

export function useDeleteTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (timesheetId: string) => timesheetApiClient.delete(timesheetId),
    meta: { suppressErrorToast: true, errorContext: 'delete timesheet' },
    onSettled: () => invalidateFor('timesheet.write', qc),
  });
}
